/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

import { Linking, Platform } from 'react-native'
import { Sentry } from '@vatom/sdk/core'
import { translate } from '@vatom/utils'
import { VatomApiType } from '@vatom/vatom/plugin'

// import { History } from 'history'
import Alert from '../components/Alert'
import { AppNavigation, AppRoutes, AppStackParamList } from '../navigators'

/** This class manages executing deep links */
export default class DeepLinkManager {
  /**
   *  Checks if a link is a valid deep link
   *
   *  @param {URL} url The URL to check.
   *  @returns {bool} True if the link is a deep link we can handle.
   */
  static isDeepLink(url: URL) {
    // Check scheme
    if (url.protocol !== 'http:' && url.protocol !== 'https:' && url.protocol !== 'redeem:')
      return false

    // Supported!
    return true
  }

  /**
   *  Call this when a vatom deep-link URL is opened. Will handle acquiring the vatom etc.
   *
   *  @param {URL} url The URL to process.
   */
  static async handleDeepLink(url: URL, vatomApi: VatomApiType, navigation: AppNavigation) {
    // if it is a redeem request
    if (url.protocol === 'redeem:') {
      const linkparts = url.href.split('://')
      const vatom = linkparts[1]

      const payloadtoredeem = {
        object_id: vatom
      }

      await vatomApi.client
        .request('POST', '/v1/user/vatom/request/redemption', payloadtoredeem, true)
        .then(() => {
          Alert.fire({
            // icon: 'info',
            title: translate('deeplink.redemptionSent'),
            text: translate('deeplink.waitingRedemption')
          })
        })
        .catch(error => {
          console.error('DeeplinkManager: Redemption error', error)
          Alert.fire({
            title: translate('errors.redemptionFailed'),
            text: translate('errors.redemptionFailedDesc')
          })
        })

      return
    }

    // If the path includes 'emit' then follow the redirect
    if (url.pathname.includes('emit')) {
      try {
        url.searchParams.append('json', 'true')
        const response = await fetch(url.toString())
        const data = await response.json()
        console.log('acquire link', data.acquireLink)
        if (data.acquireLink) {
          const acquireUrl = new URL(data.acquireLink)

          type AcquireScreenParams = AppStackParamList['Acquire']
          type AcquireUrlParams = Omit<AcquireScreenParams, 'id' | 'business-id'>

          const params = Object.fromEntries(acquireUrl.searchParams) as AcquireUrlParams
          if (acquireUrl.pathname.startsWith('/b/')) {
            // i.e Pathname "/b/U9vBRMLnBG/acquire/7d51f53c-5a95-4570-984c-941498a536d4"
            const [_, __, businessId, ___, id] = acquireUrl.pathname.split('/')

            navigation.navigate(AppRoutes.Acquire_Business, {
              ...params,
              id,
              businessId
            })
          } else {
            // i.e Pathname "/acquire/7d51f53c-5a95-4570-984c-941498a536d4"
            const [_, __, id] = acquireUrl.pathname.split('/')
            navigation.navigate(AppRoutes.Acquire, {
              ...params,
              id
            })
          }
        } else {
          throw new Error(translate('errors.noAcquireLink'))
        }
      } catch (error) {
        console.error('DeeplinkManager: Acquire error', error)
        Sentry.captureError(new Error(error as string))
        Alert.fire({
          title: translate('errors.default'),
          text: translate('errors.qrNotValid')
        })
      }
      return
      // Linking.openURL(url.toString())
    }

    // If the path includes 'emit' then follow the redirect
    if (url.pathname.includes('acquire')) {
      try {
        type AcquireScreenParams = AppStackParamList['Acquire']
        type AcquireUrlParams = Omit<AcquireScreenParams, 'id' | 'business-id'>

        const params = Object.fromEntries(url.searchParams) as AcquireUrlParams
        if (url.pathname.startsWith('/b/')) {
          // i.e Pathname "/b/U9vBRMLnBG/acquire/7d51f53c-5a95-4570-984c-941498a536d4"
          const [_, __, businessId, ___, id] = url.pathname.split('/')

          navigation.navigate(AppRoutes.Acquire_Business, {
            ...params,
            id,
            businessId
          })
        } else {
          // i.e Pathname "/acquire/7d51f53c-5a95-4570-984c-941498a536d4"
          const [_, __, id] = url.pathname.split('/')
          navigation.navigate(AppRoutes.Acquire, {
            ...params,
            id
          })
        }
      } catch (error) {
        console.error('DeeplinkManager: Acquire error', error)
        Sentry.captureError(new Error(error as string))
        Alert.fire({
          title: translate('errors.default'),
          text: translate('errors.qrNotValid')
        })
      }
      return
      // Linking.openURL(url.toString())
    }

    // If the host includes 'link' then follow the redirect
    if (url.host.includes('link')) {
      Linking.openURL(url.toString())
      return
    }

    // let route
    // if (url.hash) {
    //   // This is the old URL format. Stitch the new one together by removing the hash
    //   route = url.hash.split('#').pop() + url.search
    // } else {
    //   route = url.pathname + url.search
    // }

    // Alert.quickFire('Not implemented')
    // props.history.push(route)
    const urlString = url.toString()

    const openLink: any = await Alert.fire({
      title: translate('common.openLink'),
      text: translate('deeplink.openUrl', { url: url.hostname }),
      // icon: 'question',
      showCancelButton: true
    })

    if (openLink && openLink.isConfirmed) {
      Linking.openURL(urlString)
    }
  }
}
